import { useQueries, useQuery } from '@tanstack/react-query';
import apiRequest from 'lib/API/wrapper';
import { jstSections, FieldOption } from 'components/JSTcomponents/jstSectionsConfig';
import { BRANCH_OPTIONS } from 'constants/militaryOptions';
import { EDUCATION_LEVEL_OPTIONS } from 'constants/educationLevelOptions';

export const fetchData = async ({ queryKey }: { queryKey: string[] }) => {
  const url = queryKey[0];
  return apiRequest('GET', url);
};

interface Skill {
  id: string;
  name: string;
}

interface Field {
  soc: string;
  title: string;
}

interface MilitaryField {
  soc_code: string;
  soc_title: string;
  moc_code: string;
  moc_title: string;
}

export interface Experience {
  title: string;
  /** Raw API field: focus/role/major text (prod schema stores it here, title is always null). */
  description?: string | null;
  employer_provider: string;
  skills: Skill[];
  start_date: string;
  end_date: string | null;
  type:
    | 'employment'
    | 'apprenticeship'
    | 'internship'
    | 'degree'
    | 'assessment'
    | 'badge'
    | 'course'
    | 'militaryCourse'
    | 'certificate'
    | 'certification'
    | 'license'
    | 'apperenticeshipRTI';
  fields: Field[];
  field: Field;
  level: string | null;
  comments: string | null;
  hidden?: boolean;
  /** Raw credential key for JST academic degrees (e.g. "apprenticeshipRTI"). Preserved for edit form pre-fill. */
  education_experience_credential?: string;
  is_current?: boolean;
}

export interface MilitaryExperience {
  title: string;
  branch: string;
  rank: string;
  skills: Skill[];
  start_date: string;
  end_date: string | null;
  comments: string | null;
  is_current?: boolean;
  field?: Field | MilitaryField | null;
  hidden?: boolean;
}

interface Header {
  name: string;
  email: string;
  phone_number?: string;
  location: string;
  url?: string;
  isMilitary: boolean;
  canAddMilitary: boolean;
  showMilitaryStatus: boolean;
}

export interface Reference {
  type: 'personal' | 'professional' | '';
  name: string;
  phone_number?: string;
  title_or_role?: string;
  employer?: string;
  email?: string;
}

const VALID_WORK_EXPERIENCE_TYPES: Experience['type'][] = ['employment', 'apprenticeship', 'internship'];

interface JstSkill {
  type?: string;
  name?: string;
  id?: string;
  rating?: number;
}

interface JstMilitaryCourse {
  course_title?: string;
  institution_provider?: string;
  start_date?: string | null;
  end_date?: string | null;
  currently_enrolled?: boolean;
  description?: string | null;
  parsed_skills?: JstSkill[];
}

interface JstMilitaryExp {
  experience_title?: string;
  occupation_id?: string;
  branch_of_service?: string;
  rank?: string;
  start_date?: string | null;
  end_date?: string | null;
  currently_enrolled?: boolean | null;
  description?: string | null;
  parsed_skills?: JstSkill[];
}

interface JstOtherLearning {
  title?: string;
  institution_provider?: string;
  date_taken?: string | null;
  parsed_skills?: JstSkill[];
}

interface JstAcademicCourse {
  title?: string;
  institution?: { name?: string } | null;
  start_date?: string | null;
  end_date?: string | null;
  currently_enrolled?: boolean;
  parsed_skills?: JstSkill[];
}

interface JstAcademicDegree {
  education_experience_credential?: string;
  institution_provider?: string;
  name?: string;
  start_date?: string | null;
  end_date?: string | null;
  currently_enrolled?: boolean;
  skills_gained?: JstSkill[];
}

interface JstData {
  personal?: { branch?: string } | null;
  military_courses?: JstMilitaryCourse[];
  military_experience?: JstMilitaryExp[];
  other_learning?: JstOtherLearning[];
  academic_courses?: JstAcademicCourse[];
  academic_degrees_certificates?: JstAcademicDegree[];
}

function findFieldOptions(sectionId: string, fieldName: string, subSectionId?: string): FieldOption[] {
  const section = jstSections.find((s) => s.id === sectionId);
  if (!section) return [];
  if (section.fields && !subSectionId) {
    const field = section.fields.find((f) => f.name === fieldName);
    if (field?.options) return field.options;
  }
  if (section.subSections) {
    for (const sub of section.subSections) {
      if (subSectionId && sub.id !== subSectionId) continue;
      const field = sub.fields.find((f) => f.name === fieldName);
      if (field?.options) return field.options;
    }
  }
  return [];
}

function labelFor(value: string | null | undefined, options: FieldOption[]): string {
  if (!value) return '';
  return options.find((o) => o.value === value)?.label || value;
}

function resolveJstBranch(jstBranch: string | null | undefined): string {
  if (!jstBranch) return '';
  const opt = BRANCH_OPTIONS.find(
    (b: { apiValue?: string; value: string; backendName: string }) =>
      b.apiValue === jstBranch || b.value === jstBranch || b.backendName === jstBranch
  );
  return opt?.value ?? '';
}

function normalizeSkills(raw: JstSkill[] | undefined | null): Skill[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((s) => ({ name: s?.name || '', id: s?.id || '' })).filter((s) => s.name);
}

const EMPTY_FIELD: Field = { soc: '', title: '' };

export type Section =
  | { type: 'summary'; name: string; items: string[] }
  | { type: 'skills'; name: string; items: Skill[] }
  | { type: 'work_experience' | 'others'; name: string; items: Experience[] }
  | { type: 'military_experience'; name: string; items: MilitaryExperience[] }
  | { type: 'references'; name: string; items: Reference[] };

export interface UseResumeDataQuery {
  data: {
    header: Header;
    sections: Section[];
  };
  isLoading: boolean;
}

function useResumeDataQuery(): UseResumeDataQuery {
  const results = useQueries({
    queries: [
      { queryKey: ['/users/me'], queryFn: fetchData },
      { queryKey: ['/users/me/experience/'], queryFn: fetchData },
      { queryKey: ['/users/me/location/'], queryFn: fetchData },
    ],
  });

  const zip = results[2].data?.zip_code;
  const citiesResult = useQuery({
    queryKey: [`/cities/us?zipcode=${zip}&ordering=name`],
    queryFn: fetchData,
    enabled: !!(zip && /^\d{5}$/.test(String(zip))),
  });

  const isLoading = results.some((result) => result.isInitialLoading) || citiesResult.isInitialLoading;

  const jstData: JstData | undefined = results[1].data?.jst;

  const isMilitary =
    results[1].data?.current_military_service === true ||
    results[1].data?.previous_military_service === true ||
    (Array.isArray(jstData?.military_experience) && (jstData?.military_experience?.length ?? 0) > 0);

  const canAddMilitary =
    results[1].data?.current_military_service === true || results[1].data?.previous_military_service === true;

  const header: Header =
    results[0].data && results[2].data
      ? {
          name: `${results[0].data.first_name} ${results[0].data.last_name}`,
          email: results[0].data.email,
          phone_number: results[0].data.phone_number,
          location: (() => {
            const cities: { city: string; id: string; state_id: string }[] = Array.isArray(citiesResult.data)
              ? citiesResult.data
              : [];
            const match = cities.find((city) => String(city.id) === String(results[2].data.current_city));
            if (!match) return '';
            return `${match.city}, ${match.state_id}`;
          })(),
          url: '',
          isMilitary,
          canAddMilitary,
          showMilitaryStatus: isMilitary,
        }
      : {
          name: '',
          email: '',
          phone_number: '',
          location: '',
          isMilitary: false,
          canAddMilitary: false,
          showMilitaryStatus: false,
        };

  const jobExperience: Experience[] = results[1].data?.job_experience ?? [];

  const work_experience: Experience[] = [
    ...jobExperience
      .filter((experience: Experience) => VALID_WORK_EXPERIENCE_TYPES.includes(experience.type))
      .map((experience: Experience) => ({
        ...experience,
        field: experience.fields?.[0],
        // Prod schema: description = focus/role text, comments = tell-me-about, title = null.
        title: experience.description ?? experience.title ?? '',
        comments: experience.comments ?? null,
      })),
  ];

  const toMMYYYY = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    if (/^\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-\d{2}/);
    if (isoMatch) return `${isoMatch[2]}/${isoMatch[1]}`;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  // JST: Military Courses → Education.
  // Description is intentionally NOT carried into the resume; the JST source
  // record keeps it for editing, but the rendered resume omits it to match
  // the behavior of every other JST education mapping below.
  const jstMilitaryCourses: Experience[] = Array.isArray(jstData?.military_courses)
    ? (jstData?.military_courses ?? []).map((mc) => ({
        title: mc.course_title || '',
        employer_provider: mc.institution_provider || '',
        skills: normalizeSkills(mc.parsed_skills),
        start_date: toMMYYYY(mc.start_date),
        end_date: mc.currently_enrolled ? null : mc.end_date ? toMMYYYY(mc.end_date) : null,
        type: 'militaryCourse' as const,
        fields: [],
        field: EMPTY_FIELD,
        level: null,
        comments: null,
        is_current: mc.currently_enrolled === true,
      }))
    : [];

  // JST: Other Learning Experiences → Education
  const jstOtherLearning: Experience[] = Array.isArray(jstData?.other_learning)
    ? (jstData?.other_learning ?? []).map((ol) => ({
        title: ol.title || '',
        employer_provider: ol.institution_provider || '',
        skills: normalizeSkills(ol.parsed_skills),
        start_date: toMMYYYY(ol.date_taken),
        end_date: toMMYYYY(ol.date_taken) || null,
        type: 'course' as const,
        fields: [],
        field: EMPTY_FIELD,
        level: null,
        comments: null,
      }))
    : [];

  // JST: Academic Courses → Education
  const jstAcademicCourses: Experience[] = Array.isArray(jstData?.academic_courses)
    ? (jstData?.academic_courses ?? []).map((ac) => ({
        title: ac.title || '',
        employer_provider: ac.institution?.name || '',
        skills: normalizeSkills(ac.parsed_skills),
        start_date: toMMYYYY(ac.start_date),
        end_date: ac.currently_enrolled ? null : ac.end_date ? toMMYYYY(ac.end_date) : null,
        type: 'course' as const,
        fields: [],
        field: EMPTY_FIELD,
        level: null,
        comments: null,
        is_current: ac.currently_enrolled === true,
      }))
    : [];

  // JST: Academic Degrees / Certificates → Education
  const institutionProviderOpts = findFieldOptions('academic_degrees_certificates', 'institution_provider');
  const jstAcademicDegrees: Experience[] = Array.isArray(jstData?.academic_degrees_certificates)
    ? (jstData?.academic_degrees_certificates ?? []).map((ad) => {
        // Education levels (e.g. "bachDegree") are stored as type: 'degree' + a numeric
        // level, exactly like education[] rows, so the resume form's type dropdown (which
        // only knows 'degree|<id>' and the credential keys) can match and re-select them.
        const credential = ad.education_experience_credential || '';
        const levelOption = EDUCATION_LEVEL_OPTIONS.find((o) => o.value === credential);
        return {
          title: ad.name || '',
          employer_provider: labelFor(ad.institution_provider, institutionProviderOpts),
          skills: normalizeSkills(ad.skills_gained),
          start_date: toMMYYYY(ad.start_date),
          end_date: ad.currently_enrolled ? null : ad.end_date ? toMMYYYY(ad.end_date) : null,
          type: (levelOption ? 'degree' : credential || 'degree') as Experience['type'],
          fields: [],
          field: EMPTY_FIELD,
          level: levelOption ? String(levelOption.id) : null,
          comments: null,
          education_experience_credential: credential || undefined,
          is_current: ad.currently_enrolled === true,
        };
      })
    : [];

  const others_experience: Experience[] = [
    ...jobExperience
      .filter((experience: Experience) => !VALID_WORK_EXPERIENCE_TYPES.includes(experience.type))
      .map((experience: Experience) => ({
        ...experience,
        field: experience.fields?.[0],
        // Prod schema: description = focus/role text, comments = tell-me-about, title = null.
        title: experience.description ?? experience.title ?? '',
        comments: experience.comments ?? null,
      })),
    ...jstMilitaryCourses,
    ...jstOtherLearning,
    ...jstAcademicCourses,
    ...jstAcademicDegrees,
  ];

  // JST: Military Experience → MilitaryExperience rows.
  // Branch prefers per-entry branch_of_service; falls back to jst.personal.branch.
  // Both are normalized to BRANCH_OPTIONS .value format so the branch Select pre-fills.
  // Rank is stored as the raw camelCase enum value so the rank Select can match it.
  const jstBranchValue = resolveJstBranch(jstData?.personal?.branch);
  // Description is intentionally NOT carried into the resume; the JST source
  // record keeps it for editing, but the rendered resume omits it.
  const jstMilitary: MilitaryExperience[] = Array.isArray(jstData?.military_experience)
    ? (jstData?.military_experience ?? []).map((me) => ({
        title: me.experience_title || '',
        branch: resolveJstBranch(me.branch_of_service) || jstBranchValue,
        rank: me.rank || '',
        skills: normalizeSkills(me.parsed_skills),
        start_date: toMMYYYY(me.start_date),
        end_date: me.end_date ? toMMYYYY(me.end_date) : null,
        comments: null,
        is_current: me.currently_enrolled === true || (!me.currently_enrolled && !me.end_date),
        field: me.occupation_id
          ? { soc_code: me.occupation_id, soc_title: '', moc_code: '', moc_title: me.experience_title || '' }
          : null,
      }))
    : [];

  const military_experience: MilitaryExperience[] = [...jstMilitary];

  const allSkills = Array.from(
    new Map(
      [...work_experience, ...others_experience, ...military_experience].flatMap((exp) =>
        exp.skills.map((skill: { name: string; id: string }) => [skill.name, skill])
      )
    ).values()
  ).slice(0, 20);

  const sections: Section[] = [
    {
      name: '',
      type: 'summary',
      items: [],
    },
    {
      name: '',
      type: 'skills',
      items: allSkills,
    },
    {
      name: '',
      type: 'military_experience',
      items: military_experience,
    },
    {
      name: '',
      type: 'work_experience',
      items: work_experience,
    },
    {
      name: '',
      type: 'others',
      items: others_experience,
    },
    {
      name: '',
      type: 'references',
      items: [],
    },
  ];

  return {
    data: {
      header,
      sections,
    },
    isLoading,
  };
}

export default useResumeDataQuery;
